import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, ChevronRight, X, GripVertical, BookOpen, Mic } from "lucide-react";
import api from "../../lib/api";
import FileUpload from "../../components/FileUpload";
import ThemedSelect from "../../components/ThemedSelect";
import { useConfirm } from "../../components/ConfirmDialog";
import ModuleVoicePicker from "../../components/ModuleVoicePicker";
import BulkVoiceModal from "../../components/BulkVoiceModal";

const LEVELS = [
    { id: "beginner", label: "Débutant" },
    { id: "intermediate", label: "Intermédiaire" },
    { id: "expert", label: "Expert" },
];
const LEVEL_OPTIONS = LEVELS.map((l) => ({ value: l.id, label: l.label }));

function ModuleEditor({ module, onClose, onChanged }) {
    const confirm = useConfirm();
    const [form, setForm] = useState({
        title: module.title || "",
        description: module.description || "",
        level: module.level || "beginner",
        cover_image: module.cover_image || "",
        duration_minutes: module.duration_minutes || 20,
        order: module.order || 0,
        is_published: module.is_published !== false,
        unlock_for_all: module.unlock_for_all === true,
        // Iter-27.43 — Voix et vitesse TTS par module (override l'admin global)
        tts_voice_id: module.tts_voice_id || "",
        tts_speed: typeof module.tts_speed === "number" ? module.tts_speed : 1.1,
        // Iter-27.50 — Audience (recruteurs / référents)
        audience: module.audience === "manager" ? "manager" : "recruiter",
    });
    const [lessons, setLessons] = useState([]);
    const [quiz, setQuiz] = useState({ title: "Quiz", questions: [] });
    const [tab, setTab] = useState("info");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/admin/modules/${module.module_id}/lessons`).then((r) => setLessons(r.data));
        api.get(`/admin/modules/${module.module_id}/quiz`).then((r) => setQuiz(r.data));
    }, [module.module_id]);

    const saveInfo = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/modules/${module.module_id}`, form);
            toast.success("Module mis à jour");
            onChanged?.();
        } catch {
            toast.error("Erreur");
        } finally {
            setSaving(false);
        }
    };

    const addLesson = async () => {
        try {
            const { data } = await api.post("/admin/lessons", {
                module_id: module.module_id,
                title: "Nouvelle leçon",
                duration: "0:00",
                video_url: "",
                description: "",
                order: lessons.length,
            });
            setLessons([...lessons, data]);
        } catch {
            toast.error("Erreur");
        }
    };

    const updateLesson = async (lesson_id, patch) => {
        try {
            const { data } = await api.patch(`/admin/lessons/${lesson_id}`, patch);
            setLessons(lessons.map((l) => (l.lesson_id === lesson_id ? data : l)));
        } catch {
            toast.error("Erreur");
        }
    };

    const deleteLesson = async (lesson_id) => {
        const target = lessons.find((l) => l.lesson_id === lesson_id);
        const ok = await confirm({
            title: "Supprimer cette leçon ?",
            description: target
                ? `« ${target.title} » sera retirée du module. Les apprenants la perdront définitivement.`
                : "Cette action est irréversible.",
            confirmLabel: "Supprimer",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/admin/lessons/${lesson_id}`);
            setLessons(lessons.filter((l) => l.lesson_id !== lesson_id));
            toast.success("Leçon supprimée");
        } catch {
            toast.error("Suppression impossible");
        }
    };

    const addQuestion = () => {
        setQuiz({
            ...quiz,
            questions: [
                ...quiz.questions,
                {
                    q: "Nouvelle question",
                    options: ["Option 1", "Option 2", "Option 3", "Option 4"],
                    correct_index: 0,
                    explanation: "",
                },
            ],
        });
    };

    const updateQuestion = (idx, patch) => {
        const newQs = [...quiz.questions];
        newQs[idx] = { ...newQs[idx], ...patch };
        setQuiz({ ...quiz, questions: newQs });
    };

    const removeQuestion = (idx) => {
        setQuiz({ ...quiz, questions: quiz.questions.filter((_, i) => i !== idx) });
    };

    const saveQuiz = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/modules/${module.module_id}/quiz`, quiz);
            toast.success("Quiz enregistré");
        } catch {
            toast.error("Erreur quiz");
        } finally {
            setSaving(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-shariti-black/80 backdrop-blur flex items-stretch sm:items-center justify-center p-0 sm:p-4"
            data-testid="module-editor-backdrop"
        >
            <motion.div
                initial={{ y: 80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 80, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-3xl bg-shariti-surface sm:rounded-3xl border border-white/10 max-h-screen sm:max-h-[92vh] overflow-y-auto"
                data-testid="module-editor"
            >
                <div className="sticky top-0 bg-shariti-surface/95 backdrop-blur z-10 p-5 lg:p-6 border-b border-white/10 flex items-center justify-between">
                    <div className="min-w-0">
                        <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                            Édition module
                        </p>
                        <h2 className="text-xl lg:text-2xl font-extrabold text-white tracking-tight truncate">
                            {form.title || module.title}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="module-editor-close"
                        className="w-10 h-10 rounded-2xl bg-white/5 flex items-center justify-center hover:bg-white/10 shrink-0"
                    >
                        <X className="w-4 h-4 text-white" />
                    </button>
                </div>

                <div className="p-5 lg:p-6 space-y-5">
                    {/* Tabs */}
                    <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
                        <div className="flex gap-2 p-1.5 glass-card rounded-full w-max">
                        {[
                            { id: "info", label: "Infos" },
                            { id: "lessons", label: `Leçons (${lessons.length})` },
                            { id: "quiz", label: `Quiz (${quiz?.questions?.length || 0})` },
                        ].map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                data-testid={`editor-tab-${t.id}`}
                                className={`px-4 py-2 rounded-full text-xs lg:text-sm font-bold whitespace-nowrap transition-all ${
                                    tab === t.id
                                        ? "bg-shariti-orange text-white"
                                        : "text-white/60 hover:text-white"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                        </div>
                    </div>

                    {/* Info tab */}
                    {tab === "info" && (
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                    Titre
                                </label>
                                <input
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    data-testid="editor-title"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                    Description
                                </label>
                                <textarea
                                    rows={3}
                                    value={form.description}
                                    onChange={(e) =>
                                        setForm({ ...form, description: e.target.value })
                                    }
                                    data-testid="editor-description"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60 resize-none"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                        Niveau
                                    </label>
                                    <ThemedSelect
                                        options={LEVEL_OPTIONS}
                                        value={form.level}
                                        onChange={(v) => setForm({ ...form, level: v })}
                                        dataTestid="editor-level"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                        Durée (min)
                                    </label>
                                    <input
                                        type="number"
                                        value={form.duration_minutes}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                duration_minutes: parseInt(e.target.value) || 0,
                                            })
                                        }
                                        data-testid="editor-duration"
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none"
                                    />
                                </div>
                            </div>
                            {/* Iter-27.50 — Audience (parcours dédié) */}
                            <div>
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-2 block">
                                    Parcours d'appartenance
                                </label>
                                <div
                                    className="inline-flex bg-white/5 border border-white/10 rounded-2xl p-1.5 gap-1"
                                    data-testid="editor-audience-toggle"
                                >
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, audience: "recruiter" })}
                                        data-testid="editor-audience-recruiter"
                                        className={`px-4 py-2 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-all ${
                                            form.audience === "recruiter"
                                                ? "bg-shariti-orange text-white"
                                                : "text-white/60 hover:text-white"
                                        }`}
                                    >
                                        Recruteurs
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, audience: "manager" })}
                                        data-testid="editor-audience-manager"
                                        className={`px-4 py-2 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-all ${
                                            form.audience === "manager"
                                                ? "bg-shariti-yellow text-shariti-black"
                                                : "text-white/60 hover:text-white"
                                        }`}
                                    >
                                        Référents
                                    </button>
                                </div>
                                <p className="text-[10px] text-white/40 mt-2 leading-relaxed">
                                    {form.audience === "manager"
                                        ? "Ce module n'apparaîtra QUE pour les utilisateurs avec le rôle Manager (référent terrain)."
                                        : "Ce module n'apparaîtra QUE pour les apprenants (recruteurs de donateurs)."}
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                        Ordre
                                    </label>
                                    <input
                                        type="number"
                                        value={form.order}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                order: parseInt(e.target.value) || 0,
                                            })
                                        }
                                        data-testid="editor-order"
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                        Statut de publication
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setForm({ ...form, is_published: !form.is_published })
                                        }
                                        data-testid="editor-publish-toggle"
                                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border transition-all ${
                                            form.is_published
                                                ? "bg-shariti-orange/15 border-shariti-orange/40 text-white"
                                                : "bg-white/5 border-white/10 text-white/60"
                                        }`}
                                    >
                                        <div className="flex flex-col items-start gap-0.5 min-w-0">
                                            <span className="text-sm font-extrabold">
                                                {form.is_published ? "Publié" : "Brouillon"}
                                            </span>
                                            <span className="text-[10px] font-light text-white/55 leading-tight">
                                                {form.is_published
                                                    ? "Visible par les apprenants"
                                                    : "Caché aux apprenants"}
                                            </span>
                                        </div>
                                        {/* iOS-style switch */}
                                        <span
                                            className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${
                                                form.is_published
                                                    ? "bg-shariti-orange"
                                                    : "bg-white/15"
                                            }`}
                                        >
                                            <span
                                                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform ${
                                                    form.is_published ? "translate-x-5" : ""
                                                }`}
                                            />
                                        </span>
                                    </button>
                                </div>
                            </div>

                            {/* Iter-8 — Déverrouillage libre par module */}
                            <button
                                type="button"
                                onClick={() =>
                                    setForm({ ...form, unlock_for_all: !form.unlock_for_all })
                                }
                                data-testid="editor-unlock-toggle"
                                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border transition-all text-left ${
                                    form.unlock_for_all
                                        ? "bg-shariti-yellow/10 border-shariti-yellow/40"
                                        : "bg-white/5 border-white/10"
                                }`}
                            >
                                <div className="flex flex-col items-start gap-0.5 min-w-0 pr-2">
                                    <span className="text-[10px] font-thin tracking-[0.2em] uppercase text-shariti-yellow">
                                        Déverrouillage libre
                                    </span>
                                    <span className="text-sm font-extrabold text-white">
                                        {form.unlock_for_all
                                            ? "Accessible à tous, à tout moment"
                                            : "Verrouillage séquentiel (par défaut)"}
                                    </span>
                                    <span className="text-[10px] font-light text-white/55 leading-tight mt-0.5">
                                        {form.unlock_for_all
                                            ? "Les apprenants peuvent ouvrir ce module sans avoir terminé le précédent."
                                            : "Les apprenants doivent terminer le module précédent pour débloquer celui-ci."}
                                    </span>
                                </div>
                                <span
                                    className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${
                                        form.unlock_for_all
                                            ? "bg-shariti-yellow"
                                            : "bg-white/15"
                                    }`}
                                >
                                    <span
                                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform ${
                                            form.unlock_for_all ? "translate-x-5" : ""
                                        }`}
                                    />
                                </span>
                            </button>

                            {/* Iter-27.43 — Voix TTS dédiée au module */}
                            <ModuleVoicePicker
                                voiceId={form.tts_voice_id}
                                speed={form.tts_speed}
                                onChangeVoice={(v) => setForm({ ...form, tts_voice_id: v })}
                                onChangeSpeed={(s) => setForm({ ...form, tts_speed: s })}
                            />

                            <FileUpload
                                kind="image"
                                currentUrl={form.cover_image}
                                onUploaded={(url) => setForm({ ...form, cover_image: url })}
                                testId="editor-cover-upload"
                                label="Image de couverture (upload)"
                            />
                            <div>
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                    ou URL externe
                                </label>
                                <input
                                    type="text"
                                    placeholder="https://..."
                                    value={form.cover_image}
                                    onChange={(e) =>
                                        setForm({ ...form, cover_image: e.target.value })
                                    }
                                    data-testid="editor-cover-url"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none"
                                />
                            </div>
                            <button
                                onClick={saveInfo}
                                disabled={saving}
                                data-testid="editor-save-info"
                                className="w-full bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                            >
                                {saving ? "Sauvegarde..." : "Enregistrer les infos"}
                            </button>
                        </div>
                    )}

                    {/* Lessons tab */}
                    {tab === "lessons" && (
                        <div className="space-y-3">
                            {lessons.map((l, i) => (
                                <details
                                    key={l.lesson_id}
                                    className="glass-card rounded-2xl"
                                    data-testid={`lesson-item-${l.lesson_id}`}
                                >
                                    <summary className="cursor-pointer flex items-center gap-3 p-3 select-none">
                                        <GripVertical className="w-4 h-4 text-white/30" />
                                        <span className="w-7 h-7 rounded-lg bg-shariti-orange/20 flex items-center justify-center text-xs font-extrabold text-shariti-orange-light">
                                            {i + 1}
                                        </span>
                                        <span className="flex-1 font-bold text-white truncate">
                                            {l.title}
                                        </span>
                                        <span className="text-xs text-white/40">{l.duration}</span>
                                        <ChevronRight className="w-4 h-4 text-white/40 transition-transform" />
                                    </summary>
                                    <div className="p-4 pt-0 space-y-3">
                                        <input
                                            value={l.title}
                                            onChange={(e) =>
                                                setLessons(
                                                    lessons.map((x) =>
                                                        x.lesson_id === l.lesson_id
                                                            ? { ...x, title: e.target.value }
                                                            : x,
                                                    ),
                                                )
                                            }
                                            placeholder="Titre"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none"
                                        />
                                        <input
                                            value={l.duration}
                                            onChange={(e) =>
                                                setLessons(
                                                    lessons.map((x) =>
                                                        x.lesson_id === l.lesson_id
                                                            ? { ...x, duration: e.target.value }
                                                            : x,
                                                    ),
                                                )
                                            }
                                            placeholder="Durée (mm:ss)"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none"
                                        />
                                        <textarea
                                            rows={2}
                                            value={l.description || ""}
                                            onChange={(e) =>
                                                setLessons(
                                                    lessons.map((x) =>
                                                        x.lesson_id === l.lesson_id
                                                            ? { ...x, description: e.target.value }
                                                            : x,
                                                    ),
                                                )
                                            }
                                            placeholder="Description"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none resize-none"
                                        />
                                        <input
                                            value={l.video_url || ""}
                                            onChange={(e) =>
                                                setLessons(
                                                    lessons.map((x) =>
                                                        x.lesson_id === l.lesson_id
                                                            ? { ...x, video_url: e.target.value }
                                                            : x,
                                                    ),
                                                )
                                            }
                                            placeholder="URL vidéo (YouTube, Vimeo, ou /api/files/...)"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none"
                                        />
                                        <FileUpload
                                            kind="video"
                                            currentUrl={
                                                l.video_url?.includes("/api/files/")
                                                    ? l.video_url
                                                    : ""
                                            }
                                            onUploaded={(url) =>
                                                setLessons(
                                                    lessons.map((x) =>
                                                        x.lesson_id === l.lesson_id
                                                            ? { ...x, video_url: url }
                                                            : x,
                                                    ),
                                                )
                                            }
                                            testId={`lesson-video-${l.lesson_id}`}
                                            label="Ou upload vidéo"
                                        />
                                        {/* Iter-27.38 — Toggle d'affichage : auto / forcer vidéo / forcer présentation */}
                                        <div className="bg-white/3 border border-white/10 rounded-xl p-3">
                                            <p className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-shariti-orange-light mb-2">
                                                Mode d'affichage côté apprenant
                                            </p>
                                            <div className="grid grid-cols-3 gap-1.5">
                                                {[
                                                    { value: "auto", label: "Auto", hint: "Vidéo si présente, sinon présentation" },
                                                    { value: "video", label: "Vidéo", hint: "Forcer la vidéo (cache les slides)" },
                                                    { value: "presentation", label: "Présentation", hint: "Forcer les slides (cache la vidéo)" },
                                                ].map((opt) => {
                                                    const active = (l.display_mode || "auto") === opt.value;
                                                    return (
                                                        <button
                                                            key={opt.value}
                                                            type="button"
                                                            onClick={() =>
                                                                setLessons(
                                                                    lessons.map((x) =>
                                                                        x.lesson_id === l.lesson_id
                                                                            ? { ...x, display_mode: opt.value }
                                                                            : x,
                                                                    ),
                                                                )
                                                            }
                                                            data-testid={`lesson-display-mode-${opt.value}-${l.lesson_id}`}
                                                            className={`text-[11px] font-bold py-2 rounded-lg transition-all border ${
                                                                active
                                                                    ? "bg-shariti-orange text-white border-shariti-orange orange-glow"
                                                                    : "bg-white/3 text-white/55 border-white/10 hover:text-white hover:border-white/25"
                                                            }`}
                                                            title={opt.hint}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-[10px] text-white/40 font-light mt-2 leading-snug">
                                                {l.display_mode === "video" ? "→ Vidéo affichée, slides cachés même si rich_content." : l.display_mode === "presentation" ? "→ Slides affichés, vidéo cachée même si URL renseignée." : "→ Vidéo si video_url valide, sinon slides si rich_content, sinon image cover."}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() =>
                                                    updateLesson(l.lesson_id, {
                                                        title: l.title,
                                                        duration: l.duration,
                                                        description: l.description,
                                                        video_url: l.video_url,
                                                        display_mode: l.display_mode || "auto",
                                                    })
                                                }
                                                data-testid={`lesson-save-${l.lesson_id}`}
                                                className="flex-1 bg-shariti-orange text-white font-bold py-2 rounded-xl text-sm hover:bg-shariti-orange-light"
                                            >
                                                Enregistrer
                                            </button>
                                            <button
                                                onClick={() => deleteLesson(l.lesson_id)}
                                                data-testid={`lesson-delete-${l.lesson_id}`}
                                                className="px-3 py-2 rounded-xl bg-white/5 hover:bg-red-500/20 text-white/70"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </details>
                            ))}
                            <button
                                onClick={addLesson}
                                data-testid="add-lesson-button"
                                className="w-full glass-card rounded-2xl py-3 font-bold text-white/70 hover:text-white hover:border-shariti-orange/40 inline-flex items-center justify-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Ajouter une leçon
                            </button>
                        </div>
                    )}

                    {/* Quiz tab */}
                    {tab === "quiz" && (
                        <div className="space-y-3">
                            <input
                                value={quiz.title}
                                onChange={(e) => setQuiz({ ...quiz, title: e.target.value })}
                                placeholder="Titre du quiz"
                                data-testid="quiz-title"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none"
                            />
                            {quiz.questions.map((q, idx) => (
                                <div
                                    key={idx}
                                    className="glass-card rounded-2xl p-4 space-y-3"
                                    data-testid={`quiz-question-${idx}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                                            Question {idx + 1}
                                        </span>
                                        <button
                                            onClick={() => removeQuestion(idx)}
                                            data-testid={`quiz-remove-q-${idx}`}
                                            className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-white/70"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <input
                                        value={q.q}
                                        onChange={(e) => updateQuestion(idx, { q: e.target.value })}
                                        placeholder="Énoncé de la question"
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white outline-none"
                                    />
                                    {q.options.map((opt, j) => (
                                        <div key={j} className="flex items-center gap-2">
                                            <button
                                                onClick={() => updateQuestion(idx, { correct_index: j })}
                                                data-testid={`quiz-correct-q${idx}-o${j}`}
                                                className={`w-7 h-7 rounded-lg flex items-center justify-center font-extrabold text-xs transition-all ${
                                                    q.correct_index === j
                                                        ? "bg-shariti-orange text-white"
                                                        : "bg-white/5 text-white/40"
                                                }`}
                                                title="Marquer comme correcte"
                                            >
                                                {String.fromCharCode(65 + j)}
                                            </button>
                                            <input
                                                value={opt}
                                                onChange={(e) => {
                                                    const newOpts = [...q.options];
                                                    newOpts[j] = e.target.value;
                                                    updateQuestion(idx, { options: newOpts });
                                                }}
                                                placeholder={`Option ${j + 1}`}
                                                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-sm text-white outline-none"
                                            />
                                        </div>
                                    ))}
                                    <input
                                        value={q.explanation || ""}
                                        onChange={(e) =>
                                            updateQuestion(idx, { explanation: e.target.value })
                                        }
                                        placeholder="Explication (optionnel)"
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white/70 outline-none"
                                    />
                                </div>
                            ))}
                            <button
                                onClick={addQuestion}
                                data-testid="add-question-button"
                                className="w-full glass-card rounded-2xl py-3 font-bold text-white/70 hover:text-white hover:border-shariti-orange/40 inline-flex items-center justify-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Ajouter une question
                            </button>
                            <button
                                onClick={saveQuiz}
                                disabled={saving}
                                data-testid="quiz-save"
                                className="w-full bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                            >
                                {saving ? "..." : "Enregistrer le quiz"}
                            </button>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
}

export default function AdminModules() {
    const confirm = useConfirm();
    const [modules, setModules] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    // Iter-27.60 — Modale d'assignation bulk d'une voix à plusieurs modules
    const [bulkVoiceOpen, setBulkVoiceOpen] = useState(false);
    // Iter-27.50 — Tab d'audience : sépare visuellement Recruteurs / Référents
    // pour que l'admin ne mélange jamais les 2 catalogues.
    const [audienceTab, setAudienceTab] = useState("recruiter"); // "recruiter" | "manager"
    const [newForm, setNewForm] = useState({
        title: "",
        description: "",
        level: "beginner",
        cover_image: "",
        duration_minutes: 20,
        order: 99,
        audience: "recruiter",
    });

    const reload = async () => {
        const r = await api.get("/modules");
        setModules(r.data);
    };

    useEffect(() => {
        reload();
    }, []);

    // Iter-27.50 — Quand on change d'onglet audience, on reset le form de création
    // pour qu'il propose la bonne audience par défaut.
    useEffect(() => {
        setNewForm((f) => ({ ...f, audience: audienceTab }));
    }, [audienceTab]);

    // Iter-27.50 — Filtrage par audience pour les 2 onglets
    const filteredModules = modules.filter((m) => {
        const aud = m.audience || "recruiter";
        return aud === audienceTab;
    });

    const create = async () => {
        if (!newForm.title.trim()) {
            toast.error("Titre requis");
            return;
        }
        try {
            await api.post("/admin/modules", newForm);
            toast.success(
                newForm.audience === "manager"
                    ? "Module référent créé"
                    : "Module recruteur créé",
            );
            setCreating(false);
            setNewForm({
                title: "",
                description: "",
                level: "beginner",
                cover_image: "",
                duration_minutes: 20,
                order: 99,
                audience: audienceTab,
            });
            reload();
        } catch {
            toast.error("Erreur");
        }
    };

    const remove = async (mid) => {
        const target = modules.find((m) => m.module_id === mid);
        const ok = await confirm({
            title: "Supprimer ce module ?",
            description: target
                ? `« ${target.title} » sera supprimé ainsi que toutes ses leçons, son quiz, et la progression des apprenants associée. Cette action est irréversible.`
                : "Toutes les leçons et la progression liée seront perdues.",
            confirmLabel: "Supprimer le module",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/admin/modules/${mid}`);
            toast.success("Module supprimé");
            reload();
        } catch {
            toast.error("Suppression impossible");
        }
    };

    // Iter-27.50 — Compteurs pour les badges des onglets
    const recruiterCount = modules.filter((m) => (m.audience || "recruiter") === "recruiter").length;
    const managerCount = modules.filter((m) => m.audience === "manager").length;

    return (
        <div className="space-y-4">
            {/* Iter-27.50 — Tabs d'audience : Recruteurs / Référents */}
            <div
                className="inline-flex bg-white/5 border border-white/10 rounded-2xl p-1.5 gap-1"
                data-testid="admin-modules-audience-tabs"
            >
                <button
                    onClick={() => setAudienceTab("recruiter")}
                    data-testid="admin-modules-tab-recruiter"
                    className={`px-4 py-2 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-all ${
                        audienceTab === "recruiter"
                            ? "bg-shariti-orange text-white orange-glow"
                            : "text-white/60 hover:text-white"
                    }`}
                >
                    Recruteurs
                    <span className="ml-2 px-1.5 py-0.5 rounded-full bg-black/30 text-[10px]">
                        {recruiterCount}
                    </span>
                </button>
                <button
                    onClick={() => setAudienceTab("manager")}
                    data-testid="admin-modules-tab-manager"
                    className={`px-4 py-2 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-all ${
                        audienceTab === "manager"
                            ? "bg-shariti-yellow text-shariti-black"
                            : "text-white/60 hover:text-white"
                    }`}
                >
                    Référents
                    <span className="ml-2 px-1.5 py-0.5 rounded-full bg-black/30 text-[10px] text-white/80">
                        {managerCount}
                    </span>
                </button>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                        {audienceTab === "manager"
                            ? "Parcours Référent"
                            : "Parcours Recruteur"}
                    </p>
                    <h2 className="text-2xl font-extrabold text-white">
                        {filteredModules.length} module{filteredModules.length > 1 ? "s" : ""}
                    </h2>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Iter-27.60 — Bouton bulk voice assign */}
                    <button
                        onClick={() => setBulkVoiceOpen(true)}
                        data-testid="admin-bulk-voice-button"
                        className="inline-flex items-center gap-2 bg-white/5 hover:bg-shariti-yellow/15 text-white border border-shariti-yellow/30 font-extrabold py-2.5 px-4 rounded-2xl transition-all"
                        title="Assigner une voix à plusieurs modules en 1 clic"
                    >
                        <Mic className="w-4 h-4 text-shariti-yellow" />
                        Voix en masse
                    </button>
                    <button
                        onClick={() => setCreating(!creating)}
                        data-testid="admin-new-module-button"
                        className={`inline-flex items-center gap-2 font-extrabold py-2.5 px-4 rounded-2xl transition-all ${
                            audienceTab === "manager"
                                ? "bg-shariti-yellow text-shariti-black hover:bg-shariti-yellow/90"
                                : "bg-shariti-orange text-white orange-glow hover:bg-shariti-orange-light"
                        }`}
                    >
                        <Plus className="w-4 h-4" />
                        {audienceTab === "manager"
                            ? "Nouveau module Référent"
                            : "Nouveau module Recruteur"}
                    </button>
                </div>
            </div>

            {creating && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-card rounded-3xl p-5 space-y-3"
                    data-testid="admin-module-create-form"
                >
                    {/* Iter-27.50 — Rappel d'audience pour éviter les mélanges */}
                    <div
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                            audienceTab === "manager"
                                ? "bg-shariti-yellow/20 text-shariti-yellow border border-shariti-yellow/40"
                                : "bg-shariti-orange/20 text-shariti-orange-light border border-shariti-orange/40"
                        }`}
                        data-testid="new-module-audience-badge"
                    >
                        Création dans le parcours{" "}
                        {audienceTab === "manager" ? "Référent" : "Recruteur"}
                    </div>
                    <input
                        type="text"
                        placeholder="Titre"
                        value={newForm.title}
                        onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
                        data-testid="new-module-title"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                    />
                    <textarea
                        placeholder="Description"
                        rows={3}
                        value={newForm.description}
                        onChange={(e) =>
                            setNewForm({ ...newForm, description: e.target.value })
                        }
                        data-testid="new-module-description"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60 resize-none"
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <ThemedSelect
                            options={LEVEL_OPTIONS}
                            value={newForm.level}
                            onChange={(v) => setNewForm({ ...newForm, level: v })}
                            dataTestid="new-module-level"
                        />
                        <input
                            type="number"
                            placeholder="Durée (min)"
                            value={newForm.duration_minutes}
                            onChange={(e) =>
                                setNewForm({
                                    ...newForm,
                                    duration_minutes: parseInt(e.target.value) || 0,
                                })
                            }
                            className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none"
                        />
                    </div>
                    <FileUpload
                        kind="image"
                        currentUrl={newForm.cover_image}
                        onUploaded={(url) => setNewForm({ ...newForm, cover_image: url })}
                        testId="new-module-cover-upload"
                        label="Image de couverture"
                    />
                    <button
                        onClick={create}
                        data-testid="new-module-submit"
                        className={`w-full font-extrabold py-3 rounded-2xl transition-all ${
                            audienceTab === "manager"
                                ? "bg-shariti-yellow text-shariti-black hover:bg-shariti-yellow/90"
                                : "bg-shariti-orange text-white orange-glow hover:bg-shariti-orange-light"
                        }`}
                    >
                        Créer le module{" "}
                        {audienceTab === "manager" ? "Référent" : "Recruteur"}
                    </button>
                </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredModules.map((m) => (
                    <div
                        key={m.module_id}
                        className="glass-card rounded-3xl overflow-hidden"
                        data-testid={`admin-module-card-${m.module_id}`}
                    >
                        {m.cover_image && (
                            <img
                                src={m.cover_image}
                                alt={m.title}
                                className="w-full h-32 object-cover"
                            />
                        )}
                        <div className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-orange/20 text-shariti-orange-light">
                                    {m.level}
                                </span>
                                <span className="text-xs text-white/40">
                                    {m.duration_minutes}min
                                </span>
                            </div>
                            <h3 className="font-extrabold text-white mb-1">{m.title}</h3>
                            <p className="text-xs text-white/50 line-clamp-2 mb-3">
                                {m.description}
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setEditing(m)}
                                    data-testid={`admin-edit-module-${m.module_id}`}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-shariti-orange/20 text-shariti-orange-light hover:bg-shariti-orange hover:text-white py-2 rounded-xl text-xs font-bold transition-all"
                                >
                                    <Edit3 className="w-3.5 h-3.5" />
                                    Éditer
                                </button>
                                <button
                                    onClick={() => remove(m.module_id)}
                                    data-testid={`admin-delete-module-${m.module_id}`}
                                    className="px-3 py-2 rounded-xl bg-white/5 hover:bg-red-500/20 transition-colors"
                                >
                                    <Trash2 className="w-3.5 h-3.5 text-white/70" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <ModuleEditor
                    module={editing}
                    onClose={() => setEditing(null)}
                    onChanged={reload}
                />
            )}
            {/* Iter-27.60 — Modale bulk voice */}
            <AnimatePresence>
                {bulkVoiceOpen && (
                    <BulkVoiceModal
                        onClose={() => setBulkVoiceOpen(false)}
                        onApplied={reload}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
